import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronsUpDown, Info, Keyboard, LogOut, UserRound } from "lucide-react";
import { useNavigate } from "react-router";

import { primaryRole } from "@/app/session/permissions";
import { useCurrentUser, useSession } from "@/app/session/useSession";
import { fullName } from "@/shared/api/schema";
import { useT } from "@/shared/i18n/useT";
import { cn } from "@/shared/lib/cn";
import { Avatar } from "@/shared/ui/Avatar";
import { menuContentClassName, menuItemClassName } from "@/shared/ui/styles";

const ITEM = cn(menuItemClassName, "[&_svg]:text-ink-muted");
const SEPARATOR = "-mx-1 my-1 h-px bg-line";

interface UserMenuProps {
  onNavigate?: () => void;
  onShowShortcuts: () => void;
  onShowAbout: () => void;
  /** Avatar only, for the folded rail. */
  compact?: boolean;
  /** The charcoal rail (default) or the light header, where it is an avatar opening downward. */
  placement?: "rail" | "header";
}

/** The signed-in person, with profile, shortcuts, about and sign out. */
export function UserMenu({
  onNavigate,
  onShowShortcuts,
  onShowAbout,
  compact = false,
  placement = "rail",
}: UserMenuProps) {
  const t = useT();
  const user = useCurrentUser();
  const { signOut } = useSession();
  const navigate = useNavigate();
  const name = fullName(user) || user.email;
  const inHeader = placement === "header";
  const avatarOnly = compact || inHeader;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t("shell.account")}
        className={cn(
          "flex items-center gap-2.5 rounded-xl text-start transition-colors outline-none",
          inHeader
            ? "rounded-full p-0.5 ring-2 ring-transparent hover:ring-line-strong focus-visible:ring-brand-ring data-[state=open]:ring-brand/40"
            : "hover:bg-rail-hover focus-visible:ring-2 focus-visible:ring-rail-accent/60 data-[state=open]:bg-rail-raised",
          !inHeader && (compact ? "p-1" : "w-full p-2"),
        )}
      >
        <Avatar
          name={name}
          src={user.picture_url}
          className={inHeader ? "size-8 text-[12px]" : "size-9 text-[13px] ring-white/10"}
        />
        <span className={cn("min-w-0 flex-1 leading-tight", avatarOnly && "sr-only")}>
          <span className="user-text block truncate text-[14px] font-semibold text-rail-ink">
            {name}
          </span>
          <span className="mt-0.5 block truncate text-[12.5px] text-rail-ink-subtle">
            {t(`role.${primaryRole(user)}`)}
          </span>
        </span>
        {avatarOnly ? null : (
          <ChevronsUpDown className="size-4 shrink-0 text-rail-ink-subtle" aria-hidden="true" />
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={inHeader ? "bottom" : "top"}
          align={inHeader ? "end" : "start"}
          sideOffset={8}
          className={cn(menuContentClassName, "min-w-60")}
        >
          <div className="px-2 py-2">
            <p className="user-text truncate text-[14px] font-medium text-ink">{name}</p>
            <p dir="ltr" className="mt-0.5 truncate text-start text-[12.5px] text-ink-subtle">
              {user.email}
            </p>
          </div>
          <DropdownMenu.Separator className={SEPARATOR} />
          <DropdownMenu.Item
            className={ITEM}
            onSelect={() => {
              onNavigate?.();
              void navigate("/me");
            }}
          >
            <UserRound aria-hidden="true" />
            {t("shell.profile")}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={ITEM} onSelect={onShowShortcuts}>
            <Keyboard aria-hidden="true" />
            {t("shell.shortcuts")}
          </DropdownMenu.Item>
          <DropdownMenu.Item className={ITEM} onSelect={onShowAbout}>
            <Info aria-hidden="true" />
            {t("shell.about")}
          </DropdownMenu.Item>
          <DropdownMenu.Separator className={SEPARATOR} />
          <DropdownMenu.Item className={ITEM} onSelect={() => void signOut()}>
            <LogOut className="rtl:-scale-x-100" aria-hidden="true" />
            {t("shell.signOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
